import { useTheme } from '@emotion/react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { ReactComponent as CloseIcon } from 'src/assets/svg/close-icon.svg';
import { Section } from 'src/components/section';
import { FieldTypes } from 'src/components/controlled-field/types';
import { ControlledField } from 'src/components/controlled-field';
import { Button } from 'src/components/button';
import { loginUser } from 'src/auth/auth0';
import { getTheme } from 'src/utils/getTheme';
import { ReactComponent as WarningIcon } from 'src/assets/svg/warning-hos.svg';

import { loginDataKeys } from '../constants';
import { loginNoPasswordValidationDataSchema } from '../login-schema';

import { styles } from './styles';

import type { LoginFormProps } from './types';
import type { LoginDataFormValues } from '../types';

export const LoginForm: FC<LoginFormProps> = ({
    closeFn,
    handleShowPasswordReset,
    currentEmail = null,
    redirectUrl,
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [serverError, setServerError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const { isHosTheme } = getTheme();
    const { control, handleSubmit, watch } = useForm<LoginDataFormValues>({
        resolver: yupResolver(loginNoPasswordValidationDataSchema) as Resolver<LoginDataFormValues>,
        defaultValues: { email: currentEmail || '', password: '' },
    });

    const watchEmailField = watch('email');
    const watchPasswordField = watch('password');

    const onSubmit = handleSubmit(async (data) => {
        setLoading(true);

        try {
            await loginUser(data.email, data.password, redirectUrl, t);
        } catch (error) {
            setServerError(error.message);
        } finally {
            setLoading(false);
        }
    });

    const handleSubmitOnEnter = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' && watchEmailField && watchPasswordField) {
            event.preventDefault();
            onSubmit();
        }
    };

    return (
        <Fragment>
            <Box css={styles.loginDialogWrapper}>
                <Section header={t('login_dialog_title')}>
                    {serverError && (
                        <Box css={styles.loginDialogErrorBox}>
                            {isHosTheme && <WarningIcon />}
                            <Typography color={theme.palette.primary.main} variant="subtitle1">
                                {serverError}
                            </Typography>
                        </Box>
                    )}

                    <Stack
                        spacing="20px"
                        css={styles.loginFormWrapper}
                        onKeyDown={handleSubmitOnEnter}
                    >
                        <ControlledField
                            type="email"
                            name={loginDataKeys.email}
                            control={control}
                            label={t('login_dialog_email_address')}
                            placeholder={t('login_dialog_email_address')}
                        />
                        <ControlledField
                            fieldType={FieldTypes.Password}
                            name={loginDataKeys.password}
                            control={control}
                            label={t('login_dialog_password')}
                            placeholder={t('login_dialog_password')}
                        />
                    </Stack>
                    <Button
                        disableElevation
                        css={styles.loginButton}
                        variant="contained"
                        onClick={onSubmit}
                        disabled={!watchEmailField || !watchPasswordField}
                        loading={loading}
                    >
                        {t('login_dialog_cta')}
                    </Button>

                    <Button
                        variant="text"
                        css={styles.forgottenPassword}
                        design="light"
                        onClick={handleShowPasswordReset}
                        noEndIcon
                    >
                        <Typography
                            css={styles.forgottenPasswordText}
                            variant={isHosTheme ? 'subtitle1' : 'subtitle2'}
                        >
                            {t('login_dialog_forgotten_password')}
                        </Typography>
                    </Button>
                </Section>
            </Box>

            <IconButton css={styles.loginDialogCloseButton} onClick={closeFn}>
                <CloseIcon />
            </IconButton>
        </Fragment>
    );
};
